class DiscountUpdateEvent extends CustomEvent {
  constructor(data, id) {
    super('discount:update', {
      detail: {data, id},
      bubbles: true,
    });
  }
}

function fetchConfig(type = 'json', options = {}) {
  return {
    method: 'POST',
    headers: {
      'Content-Type':
        type === 'json'
          ? 'application/json'
          : 'application/x-www-form-urlencoded',
      Accept: `application/${type}`,
    },
    ...options,
  };
}

function morphSection(sectionId, cartSummary, newHtml) {
  const sectionEl = document.getElementById(cartSummary);
  const newDoc = new DOMParser().parseFromString(newHtml, 'text/html');
  const newSection = newDoc.getElementById(cartSummary);

  if (sectionEl && newSection) {
    sectionEl.replaceWith(newSection);
  }
}

function cartPerformanceMark(name) {
  try {
    performance.mark(name);
  } catch (_) {}
}

/**
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError
 * @property {HTMLElement} cartDiscountErrorDiscountCode
 * @property {HTMLElement} cartDiscountErrorShipping
 */

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.applyDiscount = this.applyDiscount.bind(this);
    this.removeDiscount = this.removeDiscount.bind(this);
    this.cartDrawer = document.querySelector('cart-drawer');
    this.mainCart = document.querySelector('.main-cart');
  }

  connectedCallback() {
    this.refs = {
      cartDiscountError: this.querySelector('[ref="cartDiscountError"]'),
      cartDiscountErrorDiscountCode: this.querySelector(
        '[ref="cartDiscountErrorDiscountCode"]',
      ),
      cartDiscountErrorShipping: this.querySelector(
        '[ref="cartDiscountErrorShipping"]',
      ),
    };

    this.addEventListener('submit', this.applyDiscount);
    this.addEventListener('click', this.removeDiscount);
    this.addEventListener('keydown', this.removeDiscount);
  }

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    return controller;
  }

  async applyDiscount(event) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    if (!(event.target instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const input = event.target.querySelector('input[name="discount"]');

    const drawerSectionId = this.cartDrawer.querySelector('.cart-drawer')?.id;
    const mainCartSectionId = this.mainCart?.id;

    if (!(input instanceof HTMLInputElement) || !drawerSectionId) return;

    const discountCodeValue = input.value.trim();
    if (!discountCodeValue) return;

    const existing = this.#existingDiscounts();
    if (existing.includes(discountCodeValue)) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const abortController = this.#createAbortController();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: [...existing, discountCodeValue].join(','),
        sections: this.getSectionsToRender().map((section) => section.section),
      }),
      signal: abortController.signal,
    });

    try {
      const res = await fetch('/cart/update.js', config);
      const data = await res.json();
      const notApplicable = data.discount_codes.find(
        (d) => d.code === discountCodeValue && !d.applicable,
      );
      if (notApplicable) {
        input.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }
      if (this.cartDrawer) {
        this.updateCartSummary(
          'cart-drawer',
          'cart-drawer__footer',
          drawerSectionId,
          data,
          discountCodeValue,
          existing,
        );
      }
      if (this.mainCart) {
        this.updateCartSummary(
          this.mainCart.querySelector('#main-cart-footer').dataset.id,
          'main-cart-footer',
          mainCartSectionId,
          data,
          discountCodeValue,
          existing,
        );
      }
    } catch (err) {
      // Handle error if needed
    } finally {
      this.#activeFetch = null;
      cartPerformanceMark('discount-update:user-action');
    }
  }

  async removeDiscount(event) {
    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent || event instanceof KeyboardEvent) ||
      !(event.target instanceof HTMLElement)
    )
      return;

    const pill = event.target.closest('.cart-discount__pill');
    const drawerSectionId = this.cartDrawer.querySelector('.cart-drawer')?.id;
    const mainCartSectionId = this.mainCart?.id;

    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode || !this.dataset.sectionId) return;

    const existing = this.#existingDiscounts();
    const idx = existing.indexOf(discountCode);
    if (idx === -1) return;
    existing.splice(idx, 1);

    const abortController = this.#createAbortController();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: existing.join(','),
        sections: this.getSectionsToRender().map((section) => section.section),
      }),
      signal: abortController.signal,
    });

    try {
      const res = await fetch('/cart/update.js', config);
      const data = await res.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      if (this.cartDrawer) {
        const newHtml = data.sections['cart-drawer'];
        morphSection('cart-drawer', 'cart-drawer__footer', newHtml);
      }
      if (this.mainCart) {
        const newHtml =
          data.sections[
            this.mainCart.querySelector('#main-cart-footer').dataset.id
          ];
        morphSection(
          this.mainCart.querySelector('#main-cart-footer').dataset.id,
          'main-cart-footer',
          newHtml,
        );
      }
    } catch (_) {
      // Handle error if needed
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;
    const target =
      type === 'discount_code'
        ? cartDiscountErrorDiscountCode
        : cartDiscountErrorShipping;

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  #existingDiscounts() {
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .filter((el) => el instanceof HTMLLIElement && el.dataset.discountCode)
      .map((el) => el.dataset.discountCode);
  }
  updateCartSummary(
    cartSection,
    cartSummary,
    sectionId,
    data,
    discountCodeValue,
    existing,
  ) {
    const newHtml = data.sections[cartSection];
    const tempDoc = new DOMParser().parseFromString(newHtml, 'text/html');
    const section = tempDoc.getElementById(cartSummary);
    const codes = Array.from(
      section?.querySelectorAll('.cart-discount__pill') || [],
    )
      .map((el) => el.dataset.discountCode)
      .filter(Boolean);

    const isShippingError =
      codes.length === existing.length &&
      codes.every((code) => existing.includes(code)) &&
      data.discount_codes.some(
        (d) => d.code === discountCodeValue && d.applicable,
      );
    if (isShippingError) {
      input.value = '';
      this.#handleDiscountError('shipping');
      return;
    }

    document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

    morphSection(sectionId, cartSummary, newHtml);
  }
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
    const footerEl = document.querySelector('#main-cart-footer');
    if (footerEl) {
      sections.push({
        id: 'main-cart-footer',
        section: footerEl.dataset.id,
        selector: '.footer-js-contents',
      });
    }
    return sections;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
